import type { CalendarProps } from '@plato/ui/components/calendar';
import { DatePicker as DsDatePicker } from '@plato/ui/components/date-picker';
import { CalendarIcon, XIcon } from 'lucide-react';
import type { ReactElement } from 'react';
import { formatLongDate } from '../../lib/date-helpers.ts';
import { useLocale } from '../../lib/hooks/use-locale.ts';
import { cn } from '../../lib/utils.ts';
import { Button } from './button.tsx';
import { Text, type TextProps } from './text.tsx';

export type DatePickerProps = {
    value: Date | undefined;
    /** Heads the bottom sheet below `sm`; the anchored popover shows no header. */
    title?: string;
    valuePrefix?: string;
    onChange: (date: Date | undefined) => void;
    disableIcon?: boolean;
    textColor?: TextProps['color'];
    clearable?: boolean;
    className?: string;
} & Pick<CalendarProps, 'disabled' | 'defaultMonth' | 'startMonth' | 'endMonth'>;

export function DatePicker({
    value,
    title,
    onChange,
    valuePrefix = '',
    disableIcon = false,
    className,
    textColor = 'neutral-900',
    clearable = false,
    ...props
}: DatePickerProps): ReactElement {
    const { t, locale, dateFnsLocalization } = useLocale();

    const placeholder = t('placeholder__select_date');
    const defaultMonth = props.defaultMonth ?? (value ? new Date(value) : new Date());
    const showClear = clearable && value !== undefined && props.disabled !== true;

    return (
        <div className="relative inline-flex">
            <DsDatePicker
                {...props}
                title={title ?? placeholder}
                placeholder={placeholder}
                locale={dateFnsLocalization}
                defaultMonth={defaultMonth}
                value={value}
                onValueChange={onChange}
                render={
                    <Button
                        variant="ghost"
                        className={cn('justify-start text-left font-normal', showClear && 'pr-8', className)}
                    >
                        {!disableIcon && <CalendarIcon className="h-4 w-4 text-neutral-500" />}
                        <Text color={textColor}>
                            {value ? `${valuePrefix}${formatLongDate(value, locale)}` : placeholder}
                        </Text>
                    </Button>
                }
            />
            {showClear && (
                <Button
                    variant="ghost"
                    icon={XIcon}
                    className="absolute right-1 top-1/2 -translate-y-1/2 shrink-0 p-0 h-6 w-6 min-w-6 opacity-50 hover:opacity-100"
                    onClick={(e) => {
                        e.stopPropagation();
                        onChange(undefined);
                    }}
                />
            )}
        </div>
    );
}
